import { useRef, memo, useState, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import Pagination from '../common/pagination';
import FilterModal from './filter';
import { useEvaluationList } from '../../hooks/home/evaluationList/useEvaluationList';
import { useSubmitAll } from '../../hooks/home/evaluationList/useSubmitAll';
import { useDownload } from '../../hooks/home/evaluationList/useDownload';
import { useStatistics } from '../../hooks/home/evaluationList/useStatistics';

function EvaluationList({ onViewMarks, refreshKey, enabled = true }) {
    const filterModalRef = useRef();
    const [selectedStudents, setSelectedStudents] = useState([]);

    const {
        students,
        loading,
        currentPage,
        lastPage,
        total,
        perPage,
        appliedFilters,
        hasActiveFilters,
        handlePageChange,
        handleApplyFilter,
        handleClearFilters
    } = useEvaluationList(refreshKey, enabled);

    const { isDownloading, handleDownload } = useDownload(appliedFilters);

    const {
        isSubmitting,
        submittedImeIds,
        selectableSelectedCount,
        handleSubmit,
    } = useSubmitAll({ students, selectedStudents, setSelectedStudents, onSubmitSuccess: () => fetchStatusCounts(appliedFilters) });

    useEffect(() => {
        const lockedImeIds = students
            .filter(student => student.entry_status === 'submitted')
            .map(student => student.ime_id);

        setSelectedStudents(prev => [...new Set([...lockedImeIds, ...prev])]);
    }, [students]);

    const handleOpenFilterModal = () => filterModalRef.current?.open();

    const handleViewMarksClick = (student) => {
        if (student.entry_status === 'submitted') {
            Swal.fire({
                title: 'Entry Locked',
                text: 'This entry has been locked and cannot be modified.',
                icon: 'warning',
                confirmButtonColor: '#581f37',
            });
            return;
        }

        if (student.entry_status === 'na') {
            Swal.fire({
                title: 'Not Available',
                text: 'This entry is not available for editing.',
                icon: 'info',
                confirmButtonColor: '#581f37',
            });
            return;
        }

        const studentId = student.id;
        if (onViewMarks && studentId) {
            onViewMarks(studentId);
        } else {
            Swal.fire({
                title: 'Error',
                text: 'Unable to view student details. Student ID is missing.',
                icon: 'error',
                confirmButtonColor: '#581f37',
            });
        }
    };

    const handleCheckboxChange = useCallback((imeId, entryStatus, revert) => {
        const isRevertedStatus = entryStatus === 'reverted';
        const hasRevertFlag = revert === '1';
        if (entryStatus === 'pending') return;
        if (entryStatus !== 'draft' && !isRevertedStatus && !hasRevertFlag) return;

        setSelectedStudents(prev =>
            prev.includes(imeId) ? prev.filter(id => id !== imeId) : [...prev, imeId]
        );
    }, []);

    const handleSelectAll = useCallback((e) => {
        const permanentImeIds = students
            .filter(student =>
                student.entry_status === 'submitted' ||
                submittedImeIds.includes(student.ime_id)
            )
            .map(student => student.ime_id);

        if (e.target.checked) {
            const selectableImeIds = students
                .filter(student => {
                    const isRevertedStatus = student.entry_status === 'reverted';
                    const hasRevertFlag = student.revert === '1';
                    const isLocked = student.entry_status === 'submitted' || submittedImeIds.includes(student.ime_id);
                    return (student.entry_status === 'draft' || isRevertedStatus || hasRevertFlag) &&
                        !isLocked &&
                        student.entry_status !== 'pending';
                })
                .map(student => student.ime_id);

            setSelectedStudents([...new Set([...permanentImeIds, ...selectableImeIds])]);
        } else {
            setSelectedStudents([...new Set(permanentImeIds)]);
        }
    }, [students, submittedImeIds]);

    const selectableStudents = students.filter(student => {
        const isRevertedStatus = student.entry_status === 'reverted';
        const hasRevertFlag = student.revert === '1';
        const isLocked = student.entry_status === 'submitted' || submittedImeIds.includes(student.ime_id);
        return (student.entry_status === 'draft' || isRevertedStatus || hasRevertFlag)
            && !isLocked
            && student.entry_status !== 'pending';
    });

    const isAllSelected =
        selectableStudents.length > 0 &&
        selectableStudents.every(student => selectedStudents.includes(student.ime_id));

    const isSomeSelected =
        selectableStudents.some(student => selectedStudents.includes(student.ime_id)) &&
        !isAllSelected;

    const getRowFlags = (item) => {
        const status = item.entry_status;
        const reverted = status === 'reverted';
        const hasRevertFlag = item.revert === '1';
        const batchSubmitted = submittedImeIds.includes(item.ime_id);
        const isLocked = status === 'submitted' || batchSubmitted;
        const isNa = status === 'na';
        const isAbsent = item.attendance_status === 'absent';
        const isSelectable = (status === 'draft' || reverted || hasRevertFlag) && !batchSubmitted && !isLocked && status !== 'pending';
        const isChecked = selectedStudents.includes(item.ime_id);
        const isDisabled = !isSelectable;
        return { isLocked, isNa, isAbsent, isSelectable, isChecked, isDisabled, batchSubmitted, reverted, hasRevertFlag };
    };

    const statusBadgeConfigs = (item) => {
        const { isLocked, isNa, batchSubmitted } = getRowFlags(item);
        const hasRevertFlag = item.revert === '1';
        const isRevertedStatus = item.entry_status === 'reverted';
        const isAbsent = item.attendance_status === 'absent';

        const revertBadge = {
            className: 'text-white',
            icon: 'bi-reply-fill',
            label: 'Reverted',
            style: { backgroundColor: '#e65c00', border: '1px solid #c94f00' },
        };

        const absentBadge = {
            className: 'text-white',
            icon: 'bi-person-x-fill',
            label: 'Absent',
            style: { backgroundColor: '#6f42c1', border: '1px solid #5a32a3' },
        };

        if (isRevertedStatus) {
            const badges = [revertBadge];
            if (isAbsent) badges.push(absentBadge);
            return badges;
        }

        let primaryBadge;
        if (isLocked || batchSubmitted) {
            primaryBadge = {
                className: 'bg-success-subtle text-success border border-success-subtle',
                icon: 'bi-check-circle-fill',
                label: 'Submitted',
                style: {},
            };
        } else if (isNa) {
            primaryBadge = {
                className: 'bg-secondary-subtle text-secondary border border-secondary-subtle',
                icon: 'bi-dash-circle-fill',
                label: 'N/A',
                style: {},
            };
        } else if (item.entry_status === 'draft') {
            primaryBadge = {
                className: 'bg-info-subtle text-info border border-info-subtle',
                icon: 'bi-pencil-fill',
                label: 'Draft',
                style: {},
            };
        } else if (item.entry_status === 'pending') {
            primaryBadge = {
                className: 'bg-warning-subtle text-warning border border-warning-subtle',
                icon: 'bi-clock-fill',
                label: 'Pending',
                style: {},
            };
        } else {
            primaryBadge = {
                className: 'bg-warning-subtle text-warning border border-warning-subtle',
                icon: 'bi-clock-fill',
                label: 'Pending',
                style: {},
            };
        }

        const badges = [primaryBadge];

        if (hasRevertFlag) {
            badges.push(revertBadge);
        }

        if (isAbsent) {
            badges.push(absentBadge);
        }

        return badges;
    };

    const { statusCounts, statusLoading, fetchStatusCounts } = useStatistics();

    useEffect(() => {
        fetchStatusCounts(appliedFilters);
    }, [appliedFilters, fetchStatusCounts, refreshKey]);

    const handleStatusBadgeClick = (statusKey) => {
        const statusMap = {
            submitted: 'Submitted',
            draft: 'Draft',
            pending: 'Pending'
        };

        const currentStatus = appliedFilters?.status;
        const clickedStatus = statusMap[statusKey];

        if (currentStatus === clickedStatus) {
            handleApplyFilter({ ...appliedFilters, status: '' });
        } else {
            handleApplyFilter({ ...appliedFilters, status: clickedStatus });
        }
    };

    return (
        <>
            <div className="card">
                <div className="col-md-12">
                    <div className="card-header">
                        <div className="py-3 d-flex justify-content-between align-items-center">
                            <h5 style={{ color: '#581f37', fontWeight: 700, margin: 0 }}>Mark List</h5>

                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[
                                    { key: 'submitted', label: 'Submitted', bg: '#1f7a45', status: 'Submitted' },
                                    { key: 'draft', label: 'Draft', bg: '#1c5fb8', status: 'Draft' },
                                    { key: 'pending', label: 'Pending', bg: '#b7791f', status: 'Pending' },
                                ].map(s => {
                                    const isActive = appliedFilters?.status === s.status;
                                    return (
                                        <div
                                            key={s.key}
                                            onClick={() => handleStatusBadgeClick(s.key)}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                padding: '3px 8px 3px 5px',
                                                borderRadius: '999px',
                                                background: s.bg,
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                userSelect: 'none',
                                                boxShadow: isActive ? `0 0 0 2px ${s.bg}44` : 'none',
                                            }}
                                        >
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '16px',
                                                height: '16px',
                                                borderRadius: '999px',
                                                background: 'rgba(255,255,255,0.25)',
                                                color: '#ffffff',
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                padding: '0 3px',
                                                lineHeight: 1,
                                            }}>
                                                {statusLoading ? '…' : (statusCounts?.[s.key] ?? 0)}
                                            </span>

                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                color: '#ffffff',
                                                lineHeight: 1,
                                            }}>
                                                {s.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="row align-items-center mt-0 mb-1">
                                <div className="col-12 d-flex flex-wrap align-items-center gap-2 justify-content-between">
                                    <div className="d-flex flex-wrap gap-2">
                                        <span title={selectableSelectedCount === 0 ? 'Select at least one student mark to enable this button' : 'Submit marks'}>
                                            <button
                                                type="button"
                                                className="btn btn-primary d-flex align-items-center gap-2"
                                                disabled={selectableSelectedCount === 0 || isSubmitting}
                                                style={selectableSelectedCount === 0 ? { pointerEvents: 'none' } : undefined}
                                                onClick={handleSubmit}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    <>Submit {selectableSelectedCount > 0 && `(${selectableSelectedCount})`}</>
                                                )}
                                            </button>
                                        </span>

                                        {selectableSelectedCount > 0 && !isSubmitting && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                title="Clear selection"
                                                onClick={() => {
                                                    const lockedImeIds = students
                                                        .filter(s => s.entry_status === 'submitted')
                                                        .map(s => s.ime_id);
                                                    setSelectedStudents(lockedImeIds);
                                                }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                        <>
                                            <style>{`
                                                .dot-loader { display: inline-flex; gap: 4px; }
                                                .dot-loader span { width: 6px; height: 6px; background: white; border-radius: 50%; display: inline-block; animation: dotPulse 1.4s infinite ease-in-out; }
                                                .dot-loader span:nth-child(2) { animation-delay: 0.2s; }
                                                .dot-loader span:nth-child(3) { animation-delay: 0.4s; }
                                                @keyframes dotPulse { 0%, 80%, 100% { opacity: .3; transform: scale(.7); } 40% { opacity: 1; transform: scale(1); } }
                                            `}</style>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary text-light d-flex align-items-center gap-2"
                                                onClick={handleDownload}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <>Downloading <span className="dot-loader"><span /><span /><span /></span></>
                                                ) : (
                                                    <>Download <i className="fa fa-download ms-1" /></>
                                                )}
                                            </button>
                                        </>

                                        <button
                                            type="button"
                                            className="btn btn-primary position-relative"
                                            title="Filter"
                                            onClick={handleOpenFilterModal}
                                        >
                                            Filter <i className="fa fa-filter ms-2" />
                                            {hasActiveFilters && (
                                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                                    <i className="fa fa-info" style={{ fontSize: '10px' }} />
                                                    <span className="visually-hidden">filters applied</span>
                                                </span>
                                            )}
                                        </button>

                                        {hasActiveFilters && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                title="Clear filters"
                                                onClick={handleClearFilters}
                                            >
                                                <i className="fa fa-times" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <div className="row">
                                <div className="table-container mb-0" style={{ overflowX: 'unset' }}>
                                    <table className="display dataTable table-striped table-bordered ex-v-table">
                                        <thead>
                                            <tr className="bg-primary">
                                                <th width="6%" className="text-center">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="round-checkbox"
                                                            checked={isAllSelected}
                                                            ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                                                            onChange={handleSelectAll}
                                                            disabled={loading || selectableStudents.length === 0}
                                                            title={selectableStudents.length === 0 ? "No selectable entries" : "Select all"}
                                                        />
                                                        <span>No</span>
                                                    </div>
                                                </th>
                                                <th width="18%">Student</th>
                                                <th width="20%">Exam</th>
                                                <th className="text-center" width="18%">Discipline</th>
                                                <th className="text-center" width="18%">Course</th>
                                                <th className="text-center">CCA</th>
                                                <th className="text-center">ESE</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#581f37' }}>
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        Loading students...
                                                    </td>
                                                </tr>
                                            ) : students.length ? (
                                                students.map((item, index) => {
                                                    const { isLocked, isNa, isAbsent, isChecked, isDisabled, reverted, hasRevertFlag } = getRowFlags(item);
                                                    const badges = statusBadgeConfigs(item);
                                                    const isViewDisabled = isLocked || isNa;

                                                    return (
                                                        <tr
                                                            key={`${item.id}-${index}`}
                                                            className={
                                                                isLocked || isNa ? 'table-secondary'
                                                                    : (reverted || hasRevertFlag) ? 'table-reverted'
                                                                        : isAbsent ? 'table-absent'
                                                                            : ''
                                                            }
                                                        >
                                                            <td data-label="No" className="text-center" width="6%">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="round-checkbox"
                                                                        value={item.ime_id}
                                                                        checked={isChecked}
                                                                        onChange={() => handleCheckboxChange(item.ime_id, item.entry_status, item.revert)}
                                                                        disabled={isDisabled}
                                                                        title={
                                                                            isLocked
                                                                                ? "Entry is submitted — auto-selected"
                                                                                : isNa
                                                                                    ? "Entry not available"
                                                                                    : (reverted || hasRevertFlag)
                                                                                        ? "Select reverted entry"
                                                                                        : item.entry_status === 'pending'
                                                                                            ? "Pending entries cannot be selected"
                                                                                            : "Select student"
                                                                        }
                                                                        style={{ border: "1px solid black" }}
                                                                    />
                                                                    <span>{(currentPage - 1) * perPage + index + 1}</span>
                                                                </div>
                                                            </td>
                                                            <td data-label="Student">{item.student || '-'}</td>
                                                            <td data-label="Exam">{item.examtitle || '-'}</td>
                                                            <td data-label="Discipline" className="text-center">{item.discipline || '-'}</td>
                                                            <td data-label="Course" className="text-center">{item.course || '-'}</td>
                                                            <td data-label="CCA" className="text-center">{item.cca || '-'}</td>
                                                            <td data-label="ESE" className="text-center">{item.ese || '-'}</td>
                                                            <td data-label="Status" className="text-center">
                                                                <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                                    {badges.map((badge, bi) => (
                                                                        <span
                                                                            key={bi}
                                                                            className={`badge rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1 ${badge.className}`}
                                                                            style={{ fontSize: '0.72rem', fontWeight: 500, ...badge.style }}
                                                                        >
                                                                            <i className={`bi ${badge.icon}`} style={{ fontSize: '0.7rem' }} />
                                                                            {badge.label}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td data-label="Action" className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    title={
                                                                        isLocked ? "Entry is locked" :
                                                                            isNa ? "Entry not available" :
                                                                                (reverted || hasRevertFlag) ? "View reverted entry" :
                                                                                    "View marks"
                                                                    }
                                                                    onClick={() => handleViewMarksClick(item)}
                                                                    disabled={isViewDisabled}
                                                                    style={{ cursor: isViewDisabled ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        <i className="fa fa-inbox fa-2x text-muted mb-2" />
                                                        <p className="mb-0">No students found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="row align-items-center mt-3">
                            <div className="col-md-6 col-12 d-flex justify-content-md-start gap-2">
                                <p className="mb-0 text-muted">
                                    Showing {students.length > 0 ? (currentPage - 1) * perPage + 1 : 0} to {Math.min(currentPage * perPage, total)} of {total} entries
                                    {selectableSelectedCount > 0 && (
                                        <span className="badge bg-primary ms-2">{selectableSelectedCount} selected</span>
                                    )}
                                </p>
                            </div>
                            <div className="col-md-6 col-12 d-flex justify-content-md-end justify-content-center">
                                <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={handlePageChange} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FilterModal ref={filterModalRef} onApplyFilter={handleApplyFilter} initialFilters={appliedFilters} />

            <style>{`
                .round-checkbox { appearance:none; -webkit-appearance:none; -moz-appearance:none; width:18px; height:18px; border:2px solid #6c757d; border-radius:4px; outline:none; cursor:pointer; position:relative; background-color:white; transition:all 0.2s ease; flex-shrink:0; }
                .round-checkbox:hover:not(:disabled) { border-color:#28a745; box-shadow:0 0 0 0.15rem rgba(40,167,69,0.15); }
                .round-checkbox:checked { background-color:#28a745; border-color:#28a745; }
                .round-checkbox:checked::before { content:'✓'; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); color:white; font-size:14px; font-weight:bold; line-height:1; }
                .round-checkbox:indeterminate { background-color:#28a745; border-color:#28a745; }
                .round-checkbox:indeterminate::before { content:''; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:10px; height:2px; background-color:white; }
                .round-checkbox:disabled { opacity:0.5; cursor:not-allowed; border-color:#6c757d; background-color:#e9ecef; }
                .round-checkbox:disabled:checked { background-color:#28a745; border-color:#28a745; opacity:0.7; }
                .round-checkbox:focus { border-color:#28a745; box-shadow:0 0 0 0.25rem rgba(40,167,69,0.25); }
                .form-check-input:checked { background-color:#581f37; border-color:#581f37; }
                .form-check-input:focus { border-color:#581f37; box-shadow:0 0 0 0.25rem rgba(88,31,55,0.25); }
                .table-secondary { background-color:#f8f9fa !important; }
                tr.table-secondary:hover { background-color:#e9ecef !important; }
                tr.table-reverted { background-color:#fff3e0 !important; }
                tr.table-reverted:hover { background-color:#ffe0c2 !important; }
                tr.table-absent { background-color:#f3e8ff !important; }
                tr.table-absent:hover { background-color:#e9d5ff !important; }
                table.dataTable input, table.dataTable select { height:20px !important; }
            `}</style>
        </>
    );
}

export default memo(EvaluationList);